import { useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Mail, Send, Loader2 } from "lucide-react";
import { api, apiErr } from "../api";
import { BRAND } from "../brand";
import { Modal, WhatsAppIcon, todayISO } from "../ui";
import { handleSendResult } from "./SendResult";

/** "¿Cuándo será la próxima consulta?" — agenda la próxima cita del paciente y se la envía con enlace de confirmación. */
export default function NextAppointmentModal({ patient, onClose, onSaved }) {
  const na = patient?.next_appointment;
  const [fecha, setFecha] = useState(na?.fecha || "");
  const [hora, setHora] = useState(na?.hora || "");
  const [nota, setNota] = useState("");
  const [servicio, setServicio] = useState(na?.servicio || "");
  const [channel, setChannel] = useState(patient?.email ? "both" : "whatsapp");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!fecha) { toast.error("Elige la fecha de la próxima consulta"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/patients/${patient.id}/next-appointment`, { fecha, hora, nota, servicio, channel });
      handleSendResult(data, channel);
      onSaved && onSaved(data.appointment);
      onClose();
    } catch (e) { toast.error(apiErr(e, "No se pudo agendar")); }
    finally { setBusy(false); }
  };

  const Opt = ({ id, icon: Icon, label }) => (
    <button type="button" onClick={() => setChannel(id)} aria-pressed={channel === id}
      className={`flex-1 pn-btn py-2.5 ${channel === id ? "pn-btn-soft" : "pn-btn-ghost"}`}>
      <Icon size={16} /> {label}
    </button>
  );

  return (
    <Modal title="¿Cuándo será la próxima consulta?" subtitle={patient?.nombre} icon={CalendarClock} onClose={onClose}>
      {na && <p className="text-xs pn-sub mb-4 pn-card2 rounded-lg px-3 py-2">Tiene una cita el <b>{na.fecha}{na.hora ? ` · ${na.hora}` : ""}</b>. Al guardar se mueve a la nueva fecha y se le vuelve a enviar.</p>}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="pn-label">Fecha</label><input type="date" min={todayISO()} value={fecha} onChange={(e) => setFecha(e.target.value)} className="pn-field" /></div>
          <div><label className="pn-label">Hora</label><input type="time" value={hora} onChange={(e) => setHora(e.target.value)} className="pn-field" /></div>
        </div>
        <div>
          <label className="pn-label">Servicio</label>
          <select value={servicio} onChange={(e) => setServicio(e.target.value)} className="pn-field">
            <option value="">Control / seguimiento</option>
            {BRAND.services.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div><label className="pn-label">Nota para el paciente (opcional)</label>
          <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Ej. Traer exámenes de laboratorio" className="pn-field" /></div>
        <div>
          <label className="pn-label">Enviar al paciente por</label>
          <div className="flex gap-2">
            <Opt id="email" icon={Mail} label="Email" />
            <Opt id="whatsapp" icon={WhatsAppIcon} label="WhatsApp" />
            <Opt id="both" icon={Send} label="Ambos" />
          </div>
          {!patient?.email && channel !== "whatsapp" && <p className="text-[11px] mt-1.5" style={{ color: "var(--warn)" }}>Este paciente no tiene email en su ficha.</p>}
        </div>
        <button onClick={submit} disabled={busy} className="pn-btn pn-btn-primary w-full py-3">
          {busy ? <Loader2 size={17} className="pn-spin" /> : <Send size={17} />} {busy ? "Enviando…" : "Agendar y enviar"}
        </button>
        <p className="text-[11px] pn-muted text-center">El email sale automáticamente desde {BRAND.email}. WhatsApp abre el chat con el mensaje listo; ambos incluyen el enlace para que el paciente confirme o pida otra fecha.</p>
      </div>
    </Modal>
  );
}
